import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { IoCloseOutline } from 'react-icons/io5';
import { FiPlus } from 'react-icons/fi';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import { toast } from 'react-toastify';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import methodModel from '../../methods/methods';
import Layout from '../../components/global/layout';
import FormControl from '../../components/common/FormControl';
import shared from './shared';
import { CgArrowLeft } from 'react-icons/cg';
import { FaChevronLeft } from 'react-icons/fa';
import { RiFileList3Line } from 'react-icons/ri';

// ─── Password strength rules ───────────────────────────────────────────────
const PASSWORD_RULES = [
  { key: 'minLength', label: 'At least 8 characters',        test: (v) => v.length >= 8 },
  { key: 'uppercase', label: 'At least 1 uppercase letter',  test: (v) => /[A-Z]/.test(v) },
  { key: 'lowercase', label: 'At least 1 lowercase letter',  test: (v) => /[a-z]/.test(v) },
  { key: 'number',    label: 'At least 1 number',            test: (v) => /[0-9]/.test(v) },
  { key: 'special',   label: 'At least 1 special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const validatePassword = (v) => PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(v) }));
const isPasswordValid  = (v) => PASSWORD_RULES.every((r) => r.test(v));

function AddEditUsers() {
  const { id } = useParams();
  const [form, setform] = useState({
    firstName:       '',
    lastName:        '',
    email:           '',
    mobileNo:        '',
    dialCode:        '',
    password:        '',
    confirmPassword: '',
    role:            shared?.role,
  });
  const history    = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [eyes, setEyes] = useState({ password: false, confirmPassword: false });

  const inValidEmail = methodModel.emailvalidation(form?.email);

  // ── Password derived values ──
  const passwordFilled = form.password.length > 0;
  const passwordRules  = validatePassword(form.password);
  const passwordValid  = isPasswordValid(form.password);
  const confirmMatch   = form.confirmPassword === form.password;
  const passedCount    = passwordRules.filter((r) => r.passed).length;

  const strengthLabel =
    passedCount <= 1 ? 'Weak' : passedCount <= 3 ? 'Fair' : passedCount === 4 ? 'Good' : 'Strong';
  const strengthColor =
    passedCount <= 1 ? '#ef4444' : passedCount <= 3 ? '#f59e0b' : passedCount === 4 ? '#3b82f6' : '#22c55e';

  // Original formValidation — unchanged
  const formValidation = [
    { key: 'firstName', required: true },
    { key: 'lastName',  required: true },
    { key: 'email', required: true, message: 'Email is required', email: true },
  ];

  // Original useEffect — unchanged
  useEffect(() => {
    if (id) {
      loader(true);
      ApiClient.get(shared.detailApi, { id }).then((res) => {
        if (res.success) {
          setform({
            firstName:       res?.data?.firstName || '',
            lastName:        res?.data?.lastName  || '',
            email:           res?.data?.email     || '',
            dialCode:        res?.data?.dialCode  || '',
            mobileNo:        res?.data?.mobileNo ? (res.data.mobileNo.startsWith(res.data.dialCode) ? res.data.mobileNo : `${res.data.dialCode || ''}${res.data.mobileNo}`) : '',
            image:           res?.data?.image     || '',
            role:            res?.data?.role,
            id:              res?.data?.id || res?.data?._id,
            password:        '',
            confirmPassword: '',
          });
        }
        loader(false);
      });
    }
  }, [id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    const invalid = methodModel.getFormError(formValidation, form);
    if (invalid) return;
    const isAddMode = !id;
    if (isAddMode) {
      // Mandatory on add
      if (!passwordValid) return;
      if (!confirmMatch)  return;
    } else {
      // Optional on edit — only validate if user typed something
      if (passwordFilled) {
        if (!passwordValid) return;
        if (!confirmMatch)  return;
      }
    }

    let method = 'post';
    let url    = shared.addApi;
    const value = { ...form };
    
    if (value.dialCode && value.mobileNo.startsWith(value.dialCode)) {
      value.mobileNo = value.mobileNo.substring(value.dialCode.length);
    }
console.log("obj",value)

    try {
      delete value['userId'];
      value['role'] = value?.role?.id || value?.role?._id || value?.role;
    } catch {}

    delete value.confirmPassword;

    if (id) {
      method = 'put';
      url    = shared.editApi;
      if (!passwordFilled) {
        delete value.password;
      }
    } else {
      delete value.id;
    }

    loader(true);
    ApiClient.allApi(url, value, method).then((res) => {
      if (res.success) {
        history(`/${shared.url}`);
      }
      loader(false);
    });
  };

  // Original ImageUpload — unchanged
  const ImageUpload = (e) => {
    const { files } = e.target;
    const file = files.item(0);
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG and PNG images are allowed.');
      return;
    }
    loader(true);
    ApiClient.postFormData('upload/image?model=user', { file }).then((res) => {
      if (res.success) {
        setform({ ...form, image: res?.data?.filePath });
      }
      loader(false);
    });
  };

  return (
    <Layout>
      <form onSubmit={handleSubmit}>
        <div className="pprofile1">

          {/* ── Header — unchanged ── */}
       <div className="flex gap-3 mb-8">
            <Tooltip placement="top" title="Back">
              <Link
                to={`/${shared.url}`}
                className="min-w-[30px] w-[30px] h-[30px] rounded-full bg-[#CC9900] text-white flex items-center justify-center hover:opacity-80"
              >
                <FaChevronLeft />
              </Link>
            </Tooltip>
            <div>
              <h3 className="text-lg lg:text-2xl font-semibold text-[#221E10]">
                {form && form.id ? 'Edit' : 'Add'} {shared.addTitle}
              </h3>
                  <p className='text-sm text-[#2D2D2D]'>Fill in the details below to create a new user</p>
            </div>
          </div>

<div className='bg-white p-5 rounded-[12px] shadow-[0px_3px_14px_-8px_#888888] border'>
   <div className='mb-5'>
                <div className='flex gap-2'>
                <RiFileList3Line className="text-[#CC9900] text-xl relative top-[1px]" />
               <h4 className='text-[#2D2D2D] font-semibold'>Basic Information</h4>
                </div>
                <p className='text-sm text-[#717182]'>  Enter the essential details about the user</p>
          </div>
          <div className="grid grid-cols-12 gap-5">

            {/* ── First Name — unchanged ── */}
            <div className="col-span-12 sm:col-span-6">
              <FormControl
                type="name"
                name="firstName"
                label="First Name"
                placeholder="Enter First Name"
                value={form?.firstName}
                onChange={(e) => setform({ ...form, firstName: e })}
                required
              />
              {submitted && !form.firstName && (
                <div className="d-block text-red-600">First Name is required</div>
              )}
            </div>

            {/* ── Last Name — unchanged ── */}
            <div className="col-span-12 sm:col-span-6">
              <FormControl
                type="name"
                name="lastName"
                label="Last Name"
                placeholder="Enter Last Name"
                value={form?.lastName}
                onChange={(e) => setform({ ...form, lastName: e })}
                required
              />
              {submitted && !form.lastName && (
                <div className="d-block text-red-600">Last Name is required</div>
              )}
            </div>

            {/* ── Email — unchanged ── */}
            <div className="col-span-12 sm:col-span-6">
              <FormControl
                type="text"
                name="email"
                label="Email"
                placeholder="Enter Email"
                className={id ? 'cursor-not-allowed' : ''}
                value={form.email}
                onChange={(e) => setform({ ...form, email: e })}
                required
                disabled={!!id}
              />
              {form?.email && submitted && !inValidEmail && (
                <div className="d-block text-red-600">Please enter valid email</div>
              )}
            </div>

            {/* ── Mobile Number — unchanged ── */}
            <div className="col-span-12 sm:col-span-6 mobile_number">
              <FormControl
                type="phone"
                name="mobileNo"
                label="Mobile Number"
                value={form.mobileNo}
                onChange={(val, dialCode) => setform({ ...form, mobileNo: val, dialCode: dialCode || form.dialCode })}
              />
            </div>

            {/* ── Password ── mandatory on Add, optional on Edit ── */}
            <div className="col-span-12 sm:col-span-6">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Password{!id && <span className="text-red-500"> *</span>}
                {id && <span className="text-gray-400 text-xs font-normal ml-1">(leave blank to keep unchanged)</span>}
              </label>
              <div className="relative">
                <input
                  type={eyes.password ? 'text' : 'password'}
                  className="relative  bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed pr-10 "
                  value={form.password}
                  maxLength={16}
                  placeholder={id ? 'Enter new password (optional)' : 'Enter password'}
                  onChange={(e) => setform({ ...form, password: e.target.value, confirmPassword: e.target.value === '' ? '' : form.confirmPassword })}
                  required={!id}
                />
                <div className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer">
                  <i onClick={() => setEyes({ ...eyes, password: !eyes.password })}>
                    {eyes.password
                      ? <LuEye className="text-gray-400" />
                      : <LuEyeOff className="text-gray-400" />}
                  </i>
                </div>
              </div>
              <div style={{ paddingTop: '8px', overflow: 'hidden' }}>

                {/* Strength bar */}
                <div style={{ opacity:1, transition: 'opacity 0.2s' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <div
                        key={n}
                        style={{
                          flex: 1,
                          height: '4px',
                          borderRadius: '9999px',
                          background: n <= passedCount ? strengthColor : '#e5e7eb',
                          transition: 'background 0.25s',
                        }}
                      />
                    ))}
                  </div>
                  <p style={{ fontSize: '11px', fontWeight: 600, color: strengthColor, margin: '0 0 6px 0', transition: 'color 0.2s' }}>
                    {strengthLabel}
                  </p>
                </div>

                {/* Rule checklist — always rendered, color transitions only */}
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {passwordRules.map((rule) => (
                    <li
                      key={rule.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        lineHeight: '1.6',
                        color: !passwordFilled ? '#9ca3af' : rule.passed ? '#22c55e' : '#ef4444',
                        transition: 'color 0.2s',
                      }}
                    >
                      <span style={{ fontSize: '10px', width: '10px', flexShrink: 0 }}>
                        {!passwordFilled ? '·' : rule.passed ? '✓' : '✗'}
                      </span>
                      {rule.label}
                    </li>
                  ))}
                </ul>

              </div>
              {/* end fixed-height block */}

              {submitted && passwordFilled && !passwordValid && (
                <p style={{ fontSize: '12px', color: '#ef4444', margin: '2px 0 0' }}>
                  Password does not meet all requirements.
                </p>
              )}
              {submitted && !id && !passwordFilled && (
                <p style={{ fontSize: '12px', color: '#ef4444', margin: '2px 0 0' }}>
                  Password is required.
                </p>
              )}
            </div>

            {/* ── Confirm Password ── hidden on edit when password is empty ── */}
            <div className="col-span-12 sm:col-span-6">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Confirm Password{!id && <span className="text-red-500"> *</span>}
              </label>
              <div className="relative">
                <input
                  type={eyes.confirmPassword ? 'text' : 'password'}
                  className={`relative  bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 disabled:cursor-not-allowed pr-10 ${id && !passwordFilled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  maxLength={16}
                  disabled={id && !passwordFilled}
                  onChange={(e) => setform({ ...form, confirmPassword: e.target.value })}
                  required={!id || passwordFilled}
                />
                <div className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer">
                  <i onClick={() => { if (!id || passwordFilled) setEyes({ ...eyes, confirmPassword: !eyes.confirmPassword }); }}>
                    {eyes.confirmPassword
                      ? <LuEye className="text-gray-400" />
                      : <LuEyeOff className="text-gray-400" />}
                  </i>
                </div>
              </div>

              {/* Fixed-height confirm feedback — no reflow */}
              <div style={{ height: '20px', marginTop: '4px' }}>
                {passwordFilled && form.confirmPassword.length > 0 ? (
                  <p style={{ fontSize: '12px', color: confirmMatch ? '#22c55e' : '#ef4444', transition: 'color 0.2s', margin: 0 }}>
                    {confirmMatch ? '✓ Passwords match' : '✗ Passwords does not match'}
                  </p>
                ) : submitted && passwordFilled && !form.confirmPassword ? (
                  <p style={{ fontSize: '12px', color: '#ef4444', margin: 0 }}>
                    Please confirm your password.
                  </p>
                ) : null}
              </div>
            </div>

            {/* ── Image — unchanged ── */}
            <div className="col-span-12 sm:col-span-6">
              <label className="mb-2 block">Image (JPG/PNG)</label>
              <div className="flex flex-col rounded-lg cursor-pointer gap-6 max-sm:mx-auto">
                {form?.image ? (
                  <div className="flex flex-wrap gap-3">
                    <div className="relative">
                      <img
                        src={methodModel.userImg(form?.image)}
                        className="bg-white thumbnail !w-[100px] !h-[100px] rounded-lg shadow-lg border-[2px] border-white object-contain"
                      />
                      <IoCloseOutline
                        className="absolute -top-2 -right-2 pointer hover:text-red-600 w-5 h-5 border bg-white shadow-md rounded-[50%]"
                        onClick={() => setform({ ...form, image: '' })}
                        size={25}
                      />
                    </div>
                  </div>
                ) : (
                  <label className="flex gap-2 items-center justify-center cursor-pointer text-black-800 bg-[#fff] focus:outline-none font-medium rounded-lg text-sm px-5 py-2 border border-dashed border-gray-500 w-full">
                    <FiPlus />
                    <input id="dropzone-file" type="file" className="hidden" onChange={ImageUpload} />
                    Upload Image
                  </label>
                )}
              </div>
            </div>

          </div>
          </div>

          {/* ── Submit — unchanged ── */}
          <div className="flex gap-2 justify-end mt-6">
            <button
              type="submit"
              className="bg-primary leading-10 h-10 flex justify-center  items-center shadow-btn px-6 hover:opacity-80 text-sm text-white rounded-full gap-1 cursor-pointer min-w-[94px]"
            >
              {form && form?.id ? 'Update' : 'Save'}
            </button>
          </div>

        </div>
      </form>
    </Layout>
  );
}

export default AddEditUsers;